import { PLAGUE_HP_MAX, PLAGUE_MAX_DAMAGE_DEFAULT } from "@/config/constants";
import useEmitter, { EmitterEvents, TileLabelAction } from "@/hooks/useEmitter";
import useQuestStore from "@/store/useQuestStore";
import useResourcesStore from "@/store/useResourcesStore";
import useRuntimeStore, { SuperClickerEnum } from "@/store/useRuntimeStore";
import useUserStore from "@/store/useUserStore";
import type { QuestActive } from "@/types/quest";
import type { TileCoordinates } from "@/types/tile";
import { TileEntityParam, TileLabelSkin } from "@/types/tile";
import { QuestObjectiveActions } from "./entities/config/quests/quest_objective_actions";
import { ResourceType } from "./entities/config/resources";
import { TileVisibilityType } from "./entities/config/terrain";
import { UpgradeIds } from "./entities/config/upgrades";
import QuestRepository from "./QuestRepository";
import type TileRepository from "./TileRepository";
import { getUpgradeCurrentReplaceValueById } from "./utils/upgrades";

export type PlagueTile = {
	coordinates: TileCoordinates;
	maxHp: number;
	currentHp: number;
};
class PlagueRepository {
	private emitter: ReturnType<typeof useEmitter> = useEmitter();
	private plagueCoordinates: Map<number, PlagueTile> = new Map();
	private tilerepo?: TileRepository;

	public init(tilerepo: TileRepository): void {
		this.tilerepo = tilerepo;
		const questStore = useQuestStore();
		const activePlagueQuest = questStore.getActiveQuests.find(
			(q: QuestActive): boolean =>
				q.objective.action === QuestObjectiveActions.CLEAR_PLAGUE,
		);

		if (!activePlagueQuest) {
			this.plagueCoordinates.clear();
			const userStore = useUserStore();
			userStore.setPlagueCoordinates(null);
			return;
		}

		const userStore = useUserStore();
		if (
			userStore.plagueCoordinates &&
			userStore.plagueCoordinates.length > 0
		) {
			userStore.plagueCoordinates.forEach(
				(plagueTile: PlagueTile): void => {
					const tileIndex =
						this.tilerepo?.getTileIndexByTileCoordinates(
							plagueTile.coordinates,
						);
					if (tileIndex) {
						this.plagueCoordinates.set(tileIndex, plagueTile);
						this.renderPlague();
					}
				},
			);
		}
	}

	private renderPlague(): void {
		if (this.plagueCoordinates.size === 0) return;

		for (const tile of this.plagueCoordinates.values()) {
			if (
				this.tilerepo?.getParam(
					this.tilerepo.getTileIndexByTileCoordinates(
						tile.coordinates,
					) ?? 0,
					TileEntityParam.VISIBILITY,
				) === TileVisibilityType.BLACK
			)
				continue;
			const skin =
				this.tilerepo?.getParam(
					this.tilerepo.getTileIndexByTileCoordinates(
						tile.coordinates,
					) ?? 0,
					TileEntityParam.VISIBILITY,
				) === TileVisibilityType.FOG
					? TileLabelSkin.PLAGUE_WHITE
					: TileLabelSkin.PLAGUE_GREEN;
			useEmitter().emit(EmitterEvents.PhaserUpdateTileLabel, {
				coordinates: tile.coordinates,
				skin,
				action: TileLabelAction.CREATE,
			});
			if (tile.currentHp < tile.maxHp) {
				this.emitter.emit(EmitterEvents.PlagueProgressUpdate, {
					x: tile.coordinates.x,
					y: tile.coordinates.y,
					currentHp: tile.currentHp ?? 0,
					maxHp: tile.maxHp,
					heal: false,
				});
			}
		}
	}

	public createPlague(range: number, coords: TileCoordinates): void {
		for (let i = -range; i <= range; i++) {
			for (let j = -range; j <= range; j++) {
				const baseTileIndex =
					this.tilerepo?.getTileIndexByTileCoordinates(coords);
				const tileIndex = this.tilerepo?.getTileIndexByTileCoordinates({
					x: coords.x + i,
					y: coords.y + j,
				});

				if (!tileIndex || tileIndex === baseTileIndex) continue;

				const skin =
					this.tilerepo?.getParam(
						tileIndex,
						TileEntityParam.VISIBILITY,
					) === TileVisibilityType.FOG
						? TileLabelSkin.PLAGUE_WHITE
						: TileLabelSkin.PLAGUE_GREEN;
				const coordinates = { x: coords.x + i, y: coords.y + j };
				this.plagueCoordinates.set(tileIndex, {
					coordinates,
					maxHp: PLAGUE_HP_MAX,
					currentHp: PLAGUE_HP_MAX,
				});

				if (
					tileIndex &&
					this.tilerepo?.getParam(
						tileIndex,
						TileEntityParam.VISIBILITY,
					) === TileVisibilityType.BLACK
				)
					continue;
				useEmitter().emit(EmitterEvents.PhaserUpdateTileLabel, {
					coordinates,
					skin,
					action: TileLabelAction.CREATE,
				});
			}
		}
		const userStore = useUserStore();

		userStore.setPlagueCoordinates(
			Array.from(this.plagueCoordinates.values()),
		);
	}
	public healPlague(index: number): void {
		const runtimeStore = useRuntimeStore();
		const tile = this.plagueCoordinates.get(index);

		if (!tile) return;

		let superClickerMultiplier = runtimeStore.superClickerActive
			? runtimeStore.superClickerType
			: 1;

		if (
			runtimeStore.superClickerActive &&
			runtimeStore.superClickerType === SuperClickerEnum.TEN
		) {
			superClickerMultiplier += getUpgradeCurrentReplaceValueById(
				UpgradeIds.butterfingers,
			);
		}

		const healAmount =
			Math.ceil(Math.random() * PLAGUE_MAX_DAMAGE_DEFAULT) *
			superClickerMultiplier;

		this.plagueCoordinates.set(index, {
			...tile,
			currentHp:
				tile.currentHp - healAmount > 0
					? tile.currentHp - healAmount
					: 0,
		});
		const newTile = this.plagueCoordinates.get(index);

		if (runtimeStore.superClickerActive) {
			this.decreaseSuperClickerResources(runtimeStore.superClickerType);
		}

		this.emitter.emit(EmitterEvents.PlagueProgressUpdate, {
			x: tile.coordinates.x,
			y: tile.coordinates.y,
			currentHp: newTile?.currentHp ?? 0,
			maxHp: tile.maxHp,
			heal: true,
		});

		if (newTile && newTile.currentHp <= 0) {
			this.plagueCoordinates.delete(index);
			const userStore = useUserStore();
			userStore.setPlagueCoordinates(
				Array.from(this.plagueCoordinates.values()),
			);

			useEmitter().emit(EmitterEvents.PhaserUpdateTileLabel, {
				coordinates: tile.coordinates,
				skin: TileLabelSkin.PLAGUE_GREEN,
				action: TileLabelAction.DELETE,
			});
			QuestRepository.getInstance().manageQuestState(
				QuestObjectiveActions.CLEAR_PLAGUE,
				null,
				1,
			);
			const clickedTile = this.tilerepo?.getTileEntityByTileCoordinates(
				tile.coordinates,
			);
			if (clickedTile) {
				clickedTile.exploredTimestamp = new Date().getTime();
			}
		}

		useUserStore().setPlagueCoordinates(
			Array.from(this.plagueCoordinates.values()),
		);
	}
	public changeSkin(index: number): void {
		const tile = this.plagueCoordinates.get(index);
		if (!tile) return;

		const isFow =
			this.tilerepo?.getParam(index, TileEntityParam.VISIBILITY) ===
			TileVisibilityType.FOG;

		if (isFow) {
			useEmitter().emit(EmitterEvents.PhaserUpdateTileLabel, {
				coordinates: tile.coordinates,
				skin: TileLabelSkin.PLAGUE_WHITE,
				action: TileLabelAction.CREATE,
			});
		} else {
			useEmitter().emit(EmitterEvents.PhaserUpdateTileLabel, {
				coordinates: tile.coordinates,
				skin: TileLabelSkin.PLAGUE_WHITE,
				action: TileLabelAction.DELETE,
			});
			useEmitter().emit(EmitterEvents.PhaserUpdateTileLabel, {
				coordinates: tile.coordinates,
				skin: TileLabelSkin.PLAGUE_GREEN,
				action: TileLabelAction.CREATE,
			});
		}
	}

	public plagueQuestCoordinates(
		coords: TileCoordinates,
		range: number,
	): Map<number, TileCoordinates> {
		const questCoordinates = new Map<number, TileCoordinates>();
		if (!range) return questCoordinates;

		for (let i = -range; i <= range; i++) {
			for (let j = -range; j <= range; j++) {
				const baseTileIndex =
					this.tilerepo?.getTileIndexByTileCoordinates(coords);
				const tileIndex = this.tilerepo?.getTileIndexByTileCoordinates({
					x: coords.x + i,
					y: coords.y + j,
				});

				if (!tileIndex || tileIndex === baseTileIndex) continue;
				questCoordinates.set(tileIndex, {
					x: coords.x + i,
					y: coords.y + j,
				});
			}
		}

		return questCoordinates;
	}
	private decreaseSuperClickerResources(
		superClickerMultiplier: number,
	): void {
		switch (superClickerMultiplier) {
			case 2:
				useResourcesStore().decrease(
					ResourceType.CLICKER_2,
					1,
					"map: Clicker 2 used",
				);
				QuestRepository.getInstance().manageQuestState(
					QuestObjectiveActions.USE_CLICK_MULTIPLIER,
				);
				break;
			case 3:
				useResourcesStore().decrease(
					ResourceType.CLICKER_3,
					1,
					"map: Clicker 3 used",
				);
				QuestRepository.getInstance().manageQuestState(
					QuestObjectiveActions.USE_CLICK_MULTIPLIER,
				);
				break;
			case 5:
				useResourcesStore().decrease(
					ResourceType.CLICKER_5,
					1,
					"map: Clicker 5 used",
				);
				QuestRepository.getInstance().manageQuestState(
					QuestObjectiveActions.USE_CLICK_MULTIPLIER,
				);
				break;
			default:
				useResourcesStore().decrease(
					ResourceType.SUPER_CLICKER,
					1,
					"map: super clicker used",
				);
				QuestRepository.getInstance().manageQuestState(
					QuestObjectiveActions.USE_SUPER_CLICKER,
				);
				break;
		}
	}
}

export const plagueRepository = new PlagueRepository();
